import { ApiProblem, NETWORK_PROBLEM_SLUG, slugOf, type ProblemField } from './problem';

// The single HTTP client of both SPAs (passenger and backoffice). It encodes the API's security contract so screens
// cannot forget it (API_SPECIFICATION.md §2): same-origin cookies, `X-CSRF-Token` on writes (the token is captured from
// any response that carries one), `Idempotency-Key` on POST, conditional headers, and RFC 9457 problems as ApiProblem.
// Tokens live in memory only — never in localStorage (P6.1 security).

export interface RequestOptions {
  /** Extra headers (e.g. the name of an uploaded file). */
  readonly headers?: Readonly<Record<string, string>>;
  readonly ifMatch?: string;
  readonly ifNoneMatch?: string;
  /** Reuse the SAME key when the user retries the same action (a fresh key means a new command). */
  readonly idempotencyKey?: string;
  readonly signal?: AbortSignal;
}

export interface ApiResponse<T> {
  readonly status: number;
  readonly data: T;
  readonly etag: string | undefined;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>>;
  send<T>(
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>>;
  /** POST of a raw body (e.g. a CSV file's text) with its own content type. */
  sendRaw<T>(path: string, body: string, contentType: string, options?: RequestOptions): Promise<ApiResponse<T>>;
  /** PUT of a FILE as the raw body (its own content type, its name URL-encoded in `X-File-Name`). */
  sendFile<T>(path: string, file: Blob & { readonly name: string }): Promise<ApiResponse<T>>;
  setCsrfToken(token: string | undefined): void;
  readonly baseUrl: string;
}

type Fetch = (input: string, init: RequestInit) => Promise<Response>;

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function createApiClient(
  baseUrl = '/api/v1',
  fetchImpl: Fetch = (input, init) => fetch(input, init),
): ApiClient {
  let csrfToken: string | undefined;

  async function request<T>(
    method: string,
    path: string,
    body: unknown,
    options: RequestOptions = {},
    rawContentType?: string,
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      Accept: 'application/json, application/problem+json',
      ...options.headers,
    };
    if (body !== undefined) headers['Content-Type'] = rawContentType ?? 'application/json';
    if (method !== 'GET' && csrfToken !== undefined) headers['X-CSRF-Token'] = csrfToken;
    if (method === 'POST') headers['Idempotency-Key'] = options.idempotencyKey ?? newIdempotencyKey();
    if (options.ifMatch !== undefined) headers['If-Match'] = options.ifMatch;
    if (options.ifNoneMatch !== undefined) headers['If-None-Match'] = options.ifNoneMatch;

    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, {
        method,
        headers,
        credentials: 'same-origin',
        ...(body === undefined
          ? {}
          : {
              body:
                rawContentType !== undefined && (typeof body === 'string' || body instanceof Blob)
                  ? body
                  : JSON.stringify(body),
            }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new ApiProblem(
        0,
        NETWORK_PROBLEM_SLUG,
        'No hay conexión con el servidor. Revisá tu conexión e intentá de nuevo.',
      );
    }

    const etag = response.headers.get('ETag') ?? undefined;
    if (response.status === 304) return { status: 304, data: undefined as T, etag };
    const contentType = response.headers.get('Content-Type') ?? '';
    const text = await response.text();
    const parsed: unknown = text === '' ? undefined : contentType.includes('json') ? JSON.parse(text) : text;
    if (!response.ok) throw toProblem(response, parsed);
    if (isRecord(parsed) && typeof parsed['csrfToken'] === 'string') csrfToken = parsed['csrfToken'];
    return { status: response.status, data: parsed as T, etag };
  }

  return {
    baseUrl,
    get: (path, options) => request('GET', path, undefined, options),
    send: (method, path, body, options) => request(method, path, body, options),
    sendRaw: (path, body, contentType, options) => request('POST', path, body, options, contentType),
    sendFile: (path, file) =>
      request('PUT', path, file, { headers: { 'X-File-Name': encodeURIComponent(file.name) } }, file.type),
    setCsrfToken: (token) => {
      csrfToken = token;
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toProblem(response: Response, body: unknown): ApiProblem {
  const retryAfter = Number(response.headers.get('Retry-After') ?? NaN);
  if (!isRecord(body)) {
    return new ApiProblem(response.status, 'error-desconocido', 'Ocurrió un error inesperado. Intentá de nuevo.');
  }
  const errors = Array.isArray(body['errors'])
    ? (body['errors'] as unknown[]).filter(
        (e): e is ProblemField => isRecord(e) && typeof e['pointer'] === 'string' && typeof e['detail'] === 'string',
      )
    : [];
  return new ApiProblem(
    response.status,
    slugOf(body['type']),
    typeof body['title'] === 'string' ? body['title'] : 'Ocurrió un error inesperado.',
    typeof body['detail'] === 'string' ? body['detail'] : undefined,
    errors,
    Number.isFinite(retryAfter) ? retryAfter : undefined,
  );
}
